import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { XMarkIcon } from "@heroicons/react/24/solid";
import toast from "react-hot-toast";
import { rtdb } from "../../../config/firebase";
import {
  ref,
  push,
  update,
} from "firebase/database";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

export default function EditAnggotaModal({ anggota, onSave, onClose }) {
  const isNewAnggota = anggota?.isNew || false;

  const [formData, setFormData] = useState({
    nama: anggota?.nama || "",
    jabatan: anggota?.jabatan || "",
    fotoUrl: anggota?.fotoUrl || "",
    lokasi: anggota?.lokasi || "jayapura",
    file: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const storage = getStorage(); // ✅ Firebase Storage instance
  const BUCKET_PATH = "Organisasi"; // ✅ Folder utama di bucket

  useEffect(() => {
    setFormData({
      nama: anggota?.nama || "",
      jabatan: anggota?.jabatan || "",
      fotoUrl: anggota?.fotoUrl || "",
      lokasi: anggota?.lokasi || "jayapura",
      file: null,
    });
  }, [anggota]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (formData.fotoUrl && formData.fotoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.fotoUrl);
      }
      setFormData((prev) => ({
        ...prev,
        file,
        fotoUrl: URL.createObjectURL(file),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    let finalFotoUrl = formData.fotoUrl;

    try {
      // ✅ Upload ke Firebase Storage jika ada file baru
      if (formData.file) {
        const file = formData.file;
        const fileName = `pimpinan_${Date.now()}_${file.name}`;
        const filePath = `${BUCKET_PATH}/struktur/${fileName}`;

        const fileRef = storageRef(storage, filePath);
        await uploadBytes(fileRef, file);
        finalFotoUrl = await getDownloadURL(fileRef);
      }

      // ✅ Siapkan data yang disimpan ke Realtime DB
      const dataToSave = {
        nama: formData.nama,
        jabatan: formData.jabatan,
        fotoUrl: finalFotoUrl,
        lokasi: formData.lokasi,
        timestamp: Date.now(),
      };

      let id = anggota?.id;

      if (isNewAnggota) {
        // Tambah anggota baru di node Organisasi
        const newRef = push(ref(rtdb, "Organisasi"));
        await update(newRef, dataToSave);
        id = newRef.key;
      } else {
        // Update anggota yang ada
        await update(ref(rtdb, `Organisasi/${anggota.id}`), dataToSave);
      }

      toast.success("✅ Data berhasil disimpan!");
      onSave(id, dataToSave, isNewAnggota);
      onClose();
    } catch (err) {
      console.error("❌ Gagal menyimpan data:", err);
      toast.error(err.message || "Gagal menyimpan data!");
    } finally {
      setIsSubmitting(false);
      if (formData.fotoUrl && formData.fotoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(formData.fotoUrl);
      }
    }
  };

  if (!anggota) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex justify-center items-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="flex justify-between items-center p-5 border-b">
          <h3 className="text-xl font-bold text-gray-800">
            {isNewAnggota ? "Tambah Anggota Baru" : "Edit Detail Pimpinan"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Foto */}
          <div className="flex flex-col items-center mb-6">
            {formData.fotoUrl ? (
              <img
                src={formData.fotoUrl}
                alt="Foto Profil"
                className="h-28 w-28 rounded-full object-cover mb-3 ring-4 ring-green-100 shadow-md"
              />
            ) : (
              <div className="h-28 w-28 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 mb-3">
                Tidak ada foto
              </div>
            )}
            <label className="cursor-pointer px-3 py-1.5 bg-gray-100 border border-gray-300 rounded-full text-sm text-gray-700 hover:bg-gray-200 transition">
              {isSubmitting && formData.file ? "Mengunggah..." : "Pilih Foto"}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={isSubmitting}
              />
            </label>
            <p className="text-xs text-gray-400 mt-1">
              Pilih foto dari perangkat (JPG/PNG)
            </p>
          </div>

          {/* Nama */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Nama Lengkap
            </label>
            <input
              type="text"
              name="nama"
              value={formData.nama}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Jabatan */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Jabatan/Pangkat
            </label>
            <input
              type="text"
              name="jabatan"
              value={formData.jabatan}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
              required
              disabled={isSubmitting}
            />
          </div>

          {/* Lokasi */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lokasi
            </label>
            <select
              name="lokasi"
              value={formData.lokasi}
              onChange={handleChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-green-500 focus:border-green-500"
              disabled={isSubmitting}
            >
              <option value="jayapura">Jayapura</option>
              <option value="manokwari">Manokwari</option>
            </select>
          </div>

          {/* Tombol */}
          <div className="pt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
              disabled={isSubmitting}
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-md flex items-center justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Memproses..."
                : isNewAnggota
                ? "Tambah & Simpan"
                : "Simpan Perubahan"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
